import { inject, Signal } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import {
  DialogService,
  DialogCloseReason,
  DialogCloseOptions,
  injectDirtyCloseHandler,
} from '@sui/dialog';
import { SuiDialogBase } from '@sui/dialog/sui-dialog-base';

import { UnsavedChangesDialogComponent } from 'src/app/core/components/popups/unsaved-changes-dialog/unsaved-changes-dialog.component';

import { firstValueFrom } from 'rxjs';

export abstract class SimpleDialogBase<
  TData = unknown,
  TResult = unknown,
> extends SuiDialogBase<TData, TResult> {
  protected abstract dirtyHandler: ReturnType<
    typeof injectDirtyCloseHandler<TData, TResult>
  >;

  protected dialogService = inject(DialogService);
  protected translateService = inject(TranslateService);

  // * Override to handle save when user chooses "Save & Close" on dirty dialog.
  // * Return DialogCloseOptions to customize close behavior, or void to use default Save reason
  protected performSave?(): void | DialogCloseOptions<TResult>;

  protected setupDirtyHandler(
    isDirty: Signal<boolean>,
    isValid: Signal<boolean>,
  ): void {
    this.dirtyHandler.protectClosingWhenDirty(isDirty, async () => {
      if (!isDirty()) {
        return true;
      }

      // * Open unsaved changes dialog with 3 options
      const dialogRef = this.dialogService.openSimpleDialog(
        UnsavedChangesDialogComponent,
        {
          data: {
            isValid: isValid,
          },
          closeOnClick: false,
        },
      );

      const confirmResult = await firstValueFrom(dialogRef.closed);

      if (confirmResult.reason === DialogCloseReason.SaveAndClose) {
        const result = this.performSave?.();

        // * Close with provided options or default Save reason
        const closeOptions: DialogCloseOptions<TResult> = result
          ? result
          : { reason: DialogCloseReason.Save };

        this.dirtyHandler.closeImmediately(closeOptions);

        // ! Already closed via closeImmediately
        return true;
      }

      if (confirmResult.reason === DialogCloseReason.DiscardChanges) {
        this.dirtyHandler.closeImmediately({
          reason: DialogCloseReason.DiscardChanges,
        });
        // ! close without SAVE/UPDATE
        return true;
      }

      // * User chose Cancel, ESC, or backdrop click
      // ! don't close
      return false;
    });
  }
}
